import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Upload, FileJson, CheckCircle, XCircle, AlertCircle, Database, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

const ImportDataTab = () => {
  const token = localStorage.getItem('token');
  
  const [modules, setModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [file, setFile] = useState(null);
  const [fileValidation, setFileValidation] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [overwriteMode, setOverwriteMode] = useState('update');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchModules();
    fetchStats();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/import/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModules(response.data.modules);
    } catch (error) {
      toast.error('Failed to load modules');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/import/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleFileSelect = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.json')) {
      toast.error('Please select a JSON file');
      return;
    }

    setFile(selectedFile);
    setFileValidation(null);
    setImportResults(null);

    // Validate file
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(`${API}/import/validate`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFileValidation(response.data);
      toast.success('File validated successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'File validation failed';
      toast.error(errorMsg);
      setFile(null);
    }
  };

  const toggleModule = (moduleKey) => {
    if (selectedModules.includes(moduleKey)) {
      setSelectedModules(selectedModules.filter(m => m !== moduleKey));
    } else {
      setSelectedModules([...selectedModules, moduleKey]);
    }
  };

  const selectAllAvailable = () => {
    if (fileValidation?.available_modules) {
      setSelectedModules(Object.keys(fileValidation.available_modules));
    }
  };

  const deselectAll = () => {
    setSelectedModules([]);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    if (selectedModules.length === 0) {
      toast.error('Please select at least one module');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('selected_modules', JSON.stringify(selectedModules));
    formData.append('overwrite_mode', overwriteMode);

    try {
      const response = await axios.post(`${API}/import/execute`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResults(response.data.results);
      toast.success('Import completed successfully!');
      
      // Refresh stats
      await fetchStats();
      
      // Reset selection
      setSelectedModules([]);
      setFile(null);
      setFileValidation(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Import failed';
      toast.error(errorMsg);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold flex items-center gap-3">
          <Database className="w-8 h-8 text-blue-600" />
          Import Data to Production
        </h2>
        <p className="text-gray-600 mt-2">
          Select modules you want to import data into, then upload your data_export.json file.
          This will migrate tested data from your export file to the production database.
        </p>
        
        {/* Quick Instructions */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">📝 Quick Steps:</h3>
          <ol className="space-y-1 text-sm text-blue-800">
            <li><strong>1.</strong> Upload your <code className="bg-blue-100 px-1 rounded">data_export.json</code> file below</li>
            <li><strong>2.</strong> Select which modules you want to import (checkboxes will enable after upload)</li>
            <li><strong>3.</strong> Choose import mode (Update/Skip/Replace)</li>
            <li><strong>4.</strong> Click "Start Import" and wait for completion</li>
          </ol>
          {!file && (
            <p className="mt-2 text-sm text-orange-700 font-medium">
              👉 Start by uploading your data_export.json file below
            </p>
          )}
        </div>
      </div>

      {/* Current Database Stats */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Current Database Stats
            </CardTitle>
            <CardDescription>Records currently in production database</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Object.entries(stats).map(([key, data]) => (
                <div key={key} className="flex flex-col p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">{data.label}</span>
                  <span className="text-2xl font-bold text-blue-600">{data.current_count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* File Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileJson className="w-5 h-5" />
            Step 1: Upload Data Export File
          </CardTitle>
          <CardDescription>Upload your data_export.json file</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button variant="outline" className="gap-2" asChild>
                <span>
                  <Upload className="w-4 h-4" />
                  Choose File
                </span>
              </Button>
            </label>
            {file && (
              <div className="flex items-center gap-2">
                <FileJson className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">{file.name}</span>
                <Badge variant="outline" className="text-green-600">
                  {(file.size / 1024).toFixed(2)} KB
                </Badge>
              </div>
            )}
          </div>

          {fileValidation && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-green-900">File Validated Successfully</p>
                  <p className="text-sm text-green-700 mt-1">{fileValidation.message}</p>
                  <div className="mt-2 text-sm">
                    <span className="font-medium">Available Modules: </span>
                    {Object.keys(fileValidation.available_modules).length}
                    <span className="ml-3 font-medium">Total Records: </span>
                    {fileValidation.total_records}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Module Selection - Always Visible */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Step 2: Select Modules to Import
              </CardTitle>
              <CardDescription>
                Choose which modules you want to import ({selectedModules.length} selected)
                {!fileValidation && <span className="text-orange-600 ml-2">⚠️ Upload file first to enable selection</span>}
              </CardDescription>
            </div>
            {fileValidation && (
              <div className="flex gap-2">
                <Button onClick={selectAllAvailable} variant="outline" size="sm">
                  Select All Available
                </Button>
                <Button onClick={deselectAll} variant="outline" size="sm">
                  Deselect All
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {modules.map((module) => {
              const isAvailable = fileValidation?.available_modules?.[module.key];
              const isSelected = selectedModules.includes(module.key);
              const isDisabled = !fileValidation || !isAvailable;
              
              return (
                <label
                  key={module.key}
                  className={`flex items-start gap-3 p-4 border rounded-lg transition-all ${
                    isDisabled
                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                      : isSelected 
                      ? 'border-blue-500 bg-blue-50 cursor-pointer' 
                      : 'border-gray-200 hover:border-blue-300 cursor-pointer'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => !isDisabled && toggleModule(module.key)}
                    disabled={isDisabled}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{module.label}</span>
                      {isAvailable && (
                        <Badge variant="outline" className="text-xs">
                          {isAvailable.count} records
                        </Badge>
                      )}
                      {!fileValidation && (
                        <Badge variant="outline" className="text-xs bg-gray-100">
                          Waiting for file
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{module.description}</p>
                    {fileValidation && !isAvailable && (
                      <p className="text-xs text-orange-600 mt-1">Not available in uploaded file</p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Import Options */}
      {selectedModules.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Step 3: Choose Import Mode
            </CardTitle>
            <CardDescription>How should existing records be handled?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:border-blue-300">
                <input
                  type="radio"
                  name="overwrite"
                  value="update"
                  checked={overwriteMode === 'update'}
                  onChange={(e) => setOverwriteMode(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-semibold">Update Existing (Recommended)</div>
                  <p className="text-sm text-gray-600">
                    Update existing records, add new ones. Keeps unchanged data.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:border-blue-300">
                <input
                  type="radio"
                  name="overwrite"
                  value="skip"
                  checked={overwriteMode === 'skip'}
                  onChange={(e) => setOverwriteMode(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-semibold">Skip Existing</div>
                  <p className="text-sm text-gray-600">
                    Only add new records, don't touch existing ones.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:border-yellow-300">
                <input
                  type="radio"
                  name="overwrite"
                  value="replace"
                  checked={overwriteMode === 'replace'}
                  onChange={(e) => setOverwriteMode(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-semibold text-yellow-700">Replace All</div>
                  <p className="text-sm text-gray-600">
                    Replace existing records completely with new data.
                  </p>
                </div>
              </label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Button */}
      {selectedModules.length > 0 && (
        <Card className="border-2 border-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-lg">Ready to Import</p>
                <p className="text-sm text-gray-600">
                  {selectedModules.length} modules selected with {
                    Object.values(fileValidation?.available_modules || {})
                      .filter(m => selectedModules.includes(Object.keys(fileValidation.available_modules).find(k => fileValidation.available_modules[k] === m)))
                      .reduce((sum, m) => sum + m.count, 0)
                  } total records
                </p>
              </div>
              <Button
                onClick={handleImport}
                disabled={importing}
                className="gap-2 bg-gradient-to-r from-blue-600 to-purple-600"
                size="lg"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Start Import
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Results */}
      {importResults && (
        <Card className="border-2 border-green-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-6 h-6" />
              Import Completed Successfully
            </CardTitle>
            <CardDescription>Review the import summary below</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Processed</div>
                <div className="text-2xl font-bold text-blue-600">
                  {importResults.summary.total_processed}
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Imported</div>
                <div className="text-2xl font-bold text-green-600">
                  {importResults.summary.total_imported}
                </div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Updated</div>
                <div className="text-2xl font-bold text-yellow-600">
                  {importResults.summary.total_updated}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Skipped</div>
                <div className="text-2xl font-bold text-gray-600">
                  {importResults.summary.total_skipped}
                </div>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">Errors</div>
                <div className="text-2xl font-bold text-red-600">
                  {importResults.summary.total_errors}
                </div>
              </div>
            </div>

            {/* Module Details */}
            <div className="space-y-3">
              <h4 className="font-semibold">Module Details:</h4>
              {Object.entries(importResults.imported).map(([moduleKey, results]) => {
                const module = modules.find(m => m.key === moduleKey);
                return (
                  <div key={moduleKey} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">{module?.label || moduleKey}</span>
                      <Badge variant="outline">
                        {results.processed} processed
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Imported:</span>
                        <span className="ml-2 font-medium text-green-600">{results.imported}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Updated:</span>
                        <span className="ml-2 font-medium text-yellow-600">{results.updated}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Skipped:</span>
                        <span className="ml-2 font-medium text-gray-600">{results.skipped}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Errors:</span>
                        <span className="ml-2 font-medium text-red-600">{results.errors.length}</span>
                      </div>
                    </div>
                    {results.errors.length > 0 && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-xs">
                        <div className="font-semibold text-red-700">Errors:</div>
                        {results.errors.slice(0, 3).map((err, idx) => (
                          <div key={idx} className="text-red-600">
                            • {err.record}: {err.error}
                          </div>
                        ))}
                        {results.errors.length > 3 && (
                          <div className="text-red-600">
                            ...and {results.errors.length - 3} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ImportDataTab;
